import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PuffLoader } from "react-spinners";
import useInactivityHandler from "../utils/InactivityHandler";
import { useAuth } from "../context/AuthContext";
import API_ENDPOINTS from "../config/api";

const Withdraw = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, account } = useAuth();
  const [exchangeRate, setExchangeRate] = useState(0);
  const [balance, setBalance] = useState(location.state?.balance || null);
  const [amountInUSD, setAmountInUSD] = useState("");
  const [amountInKES, setAmountInKES] = useState(0);
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [showGenerateToken, setShowGenerateToken] = useState(false);
  useInactivityHandler();

  useEffect(() => {
    const disableRightClick = (e) => e.preventDefault();
    document.addEventListener("contextmenu", disableRightClick);

    return () => {
      document.removeEventListener("contextmenu", disableRightClick);
    };
  }, []);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        if (!balance) {
          const balanceResponse = await axios.post(
            API_ENDPOINTS.GET_BALANCE,
            {account, token }
          );
          setBalance(balanceResponse.data.balance || "N/A");
        }
      } catch (error) {
        console.error("Error fetching balance:", error);
        const errorMessage = error.response?.data?.message ||
                            error.response?.data?.details ||
                            "Failed to fetch balance. Please try again later.";
        toast.error(errorMessage);
      }
    };

    const fetchWithdrawExchangeRate = async () => {
      try {
        const response = await axios.get(
          `${API_ENDPOINTS.GET_WITHDRAW_EXCHANGE_RATE}?token=${token}`
        );
        setExchangeRate(response.data.exchange_rate || 0);
      } catch (error) {
        console.error("Error fetching exchange rate:", error);
        const errorMessage = error.response?.data?.message ||
                            error.response?.data?.error ||
                            "Failed to fetch exchange rate. Please try again later.";
        toast.error(errorMessage);
      }
    };

    fetchBalance();
    fetchWithdrawExchangeRate();
  }, [token, balance,account]);

  const calculateAmountInKES = (amount) => {
    const parsedAmount = parseFloat(amount);
    const amountAfterCommission = parsedAmount * 0.97;
    setAmountInKES(Math.floor(amountAfterCommission * exchangeRate));
  };

  const handleAmountChange = (e) => {
    const amount = e.target.value;
    setAmountInUSD(amount);
    if (amount) {
      calculateAmountInKES(amount);
    } else {
      setAmountInKES(0);
    }
  };

  const handleApiError = (error) => {
    // Extract the most specific error message available
    const errorMessage =
      error.response?.data?.error ||  // Specific error from backend
      error.response?.data?.message ||  // Generic message
      error.response?.data?.error?.message ||
      error.message ||
      "An unexpected error occurred. Please try again later.";
    toast.error(errorMessage);
    console.error("API Error:", error.response?.data || error.message);
  };

  const initiateVerification = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        API_ENDPOINTS.SEND_VERIFICATION_EMAIL,
        {
          account,
          token,
        }
      );
      setLoading(false);

      if (response.data.success) {
        toast.success("Verification code sent to your email.");
        setIsCodeSent(true);
      } else {
        const error = response.data.error;
        toast.error(error?.message || "Failed to send verification code.");
        if (
          error?.code === "InvalidToken" ||
          error?.code === "AuthorizationError"
        ) {
          setShowGenerateToken(true);
        } else if (error?.code === "CashierForwardError") {
          toast.error("Your cashier is locked. Please contact Deriv support.");
        }
      }
    } catch (error) {
      setLoading(false);
      handleApiError(error);
    }
  };

  const confirmWithdrawal = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        API_ENDPOINTS.WITHDRAW,
        {
          account,
          amountInUSD: parseFloat(amountInUSD),
          token,
          amountInKES,
          verificationCode,
        }
      );

      setLoading(false);
      if (response.data.success) {
        toast.success("Withdrawal successful");
        navigate("/home");
      } else {
        const error = response.data.error;
        console.error("Withdrawal failed:", error);

        if (error?.code === "InvalidToken") {
          toast.error(
            "Your token has expired or is invalid. Please generate a new one."
          );
          setShowGenerateToken(true);
        } else if (error?.code === "PaymentAgentWithdrawError") {
          toast.error(
            error?.message || "Withdrawal failed due to restrictions."
          );
        }  else if (error?.code === "PaymentAgentWithdrawSameMethod") {
          toast.error(
            error?.message || "please select the same payment method you used to deposit."
          );
        } else {
          toast.error(error?.message || "Withdrawal failed. Please try again.");
        }
      }
    } catch (error) {
      setLoading(false);
      handleApiError(error);
    }
  };

  const generateNewToken = () => {
    navigate("/generate-token");
  };

  const isWithdrawButtonActive =
    amountInUSD &&
    parseFloat(amountInUSD) >= 3.5 &&
    parseFloat(amountInUSD) <= parseFloat(balance || 0);

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        <h1 className="text-2xl font-bold mb-4 text-center">Withdraw</h1>

        {loading ? (
          <div className="flex justify-center">
            <PuffLoader color="#36D7B7" size={60} />
          </div>
        ) : (
          <>
            <label className="block mb-2 text-gray-700">Amount in USD:</label>
            <input
              type="number"
              value={amountInUSD}
              onChange={handleAmountChange}
              className="w-full p-2 border rounded-md mb-4"
              disabled={isCodeSent}
            />
            <p className="text-lg mb-4">
              KES Equivalent: <strong>{amountInKES} KES</strong>
            </p>
            <p className="text-gray-600 mb-4">Minimum Withdrawal is 3.5 USD</p>
            <p className="text-gray-600 mb-4">
             Rate: 1 USD = {Math.floor(exchangeRate * 100) / 100} KES
            </p>
            <p className="text-gray-600 mb-4">
              Available Balance:{" "}
              {balance !== null ? `${balance} USD` : "Loading..."}
            </p>

            {isCodeSent ? (
              <>
                <label className="block mb-2 text-gray-700">
                  Verification Code:
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full p-2 border rounded-md mb-4"
                />
                <button
                  onClick={confirmWithdrawal}
                  className="w-full bg-pink-600 text-white py-2 px-4 rounded-md hover:bg-pink-700 transition"
                  disabled={!verificationCode}
                >
                  Confirm Withdrawal
                </button>
              </>
            ) : (
              <button
                onClick={initiateVerification}
                className={`w-full py-2 px-4 rounded-md transition ${
                  isWithdrawButtonActive
                    ? "bg-teal-600 text-white hover:bg-teal-700"
                    : "bg-gray-400 text-gray-700 cursor-not-allowed"
                }`}
                disabled={!isWithdrawButtonActive}
              >
                Request Verification Code
              </button>
            )}

            {showGenerateToken && (
              <button
                onClick={generateNewToken}
                className="w-full mt-4 bg-yellow-500 text-white py-2 px-4 rounded-md hover:bg-yellow-600 transition"
              >
                Generate New Token
              </button>
            )}
          </>
        )}

        <button
          onClick={() => navigate("/home")}
          className="mt-4 text-teal-600 text-lg font-semibold hover:text-teal-800 transition"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default Withdraw;
